import { MpSdk, Rotation } from 'src/types/matterport';
import get from 'lodash/get';
import { buildLink } from 'src/utils/all';
import { EventEmitter, ListenerFn } from 'eventemitter3';
import { SweepEventsType } from './enums';
import { TourObject, VSTourAsset } from 'src/sdk-components/VSObject';
import {
  GIFLoaderComponent,
  ImageComponent,
  PolygonComponent,
  UPNGLoaderComponent,
  VideoRenderer
} from 'src/sdk-components';
import { Polygon } from 'src/_models/Polygon';
import logger from 'src/logger';

class Sweep {
  sweep: any;

  on: (type: SweepEventsType, callback: ListenerFn) => void;

  emit: (type: SweepEventsType, data?: unknown) => void;

  private mpSDK: MpSdk;

  constructor(mpSDK: MpSdk) {
    this.mpSDK = mpSDK;

    this.sweep = mpSDK.Sweep;

    this.subscribe = this.subscribe.bind(this);
    this.moveTo = this.moveTo.bind(this);

    const interaction = new EventEmitter();

    this.on = function (type: SweepEventsType, callback: ListenerFn) {
      interaction.on(type, callback);
    };

    this.emit = function (type: SweepEventsType, data?: any) {
      interaction.emit(type, data);
    };
  }

  subscribe(callback: (collection: Array<Record<string, unknown>>) => void) {
    this.sweep.data.subscribe({
      onCollectionUpdated: (collection: { [s: string]: unknown } | ArrayLike<unknown>) => {
        const transformToUUID = (id: string) =>
          [id.slice(8, 12), id.slice(8, 12), id.slice(12, 16), id.slice(16, 20), id.slice(20)].join(
            '-'
          );
        const sweeps = Object.values(collection).map((item: any) => ({
          ...item,
          sweep_uuid: transformToUUID(item.id),
          floor_index: Number(get(item, 'floorInfo.id')),
          floor_position: get(item, 'position')
        }));
        callback(sweeps);
      }
    });
  }

  async moveTo(sweepId: string, rotation: Rotation, transition = 'transition.instant') {
    window.history.replaceState(null, '', buildLink(sweepId, rotation));
    await this.sweep.moveTo(sweepId, {
      rotation,
      transition: this.sweep.Transition[transition]
    });
  }

  async moveToObject(_object: VSTourAsset | Polygon, activeTourAsset: any, modelNodes: any[]) {
    if (activeTourAsset) this.emit(SweepEventsType.CancelTourObjectChanges);
    if (_object instanceof Polygon && !_object.sweep_uuid) {
      throw Error(`Can't move to the object.`);
    }

    if (!_object.active) {
      throw Error(`Object is not active.`);
    }

    const activeNode = modelNodes.find((item: any) => item.obj3D.uuid === _object.uuid);

    if (!activeNode) {
      throw Error(`Can't move to the object.`);
    }

    let activeComponent: any;
    activeNode.components.forEach((component: any) => {
      if (
        component.instance instanceof ImageComponent ||
        component.instance instanceof UPNGLoaderComponent ||
        component.instance instanceof GIFLoaderComponent ||
        component.instance instanceof VideoRenderer ||
        component.instance instanceof PolygonComponent ||
        _object instanceof TourObject
      ) {
        activeComponent = component.instance;
      }
    });

    if (!activeComponent) {
      throw Error(`Can't move to the object.`);
    }

    if (_object instanceof Polygon && _object.sweep_uuid) {
      await this.sweep.moveTo(_object.sweep_uuid, {
        rotation: {
          x: parseFloat(String(_object.srX)),
          y: parseFloat(String(_object.srY)),
          z: parseFloat(String(_object.srZ))
        },
        transition: this.sweep.Transition.INSTANT,
        transitionTime: 900
      });
    }
    // Make the transform control visible so that the user can manipulate the control selection.
    // this.vsControl.inputs.visible = true;
    // The three.js object being controlled by this component.
    // this.vsControl.inputs.selection = activeNode;

    if (_object instanceof TourObject) {
      this.emit(SweepEventsType.SetActiveNodeEditView, { state: true, activeComponent });
    }

    return {
      showActionMenu: true,
      activeTourAsset: _object,
      activeChanged: false,
      activeNode,
      activeComponent
    };
  }

  async moveToSweepIO(p: any) {
    setTimeout(() => {
      if (!p.walking) {
        this.mpSDK.Mode.moveTo(p.mode, {
          position: p.position,
          rotation: p.rotation,
          transition: this.mpSDK.Mode.TransitionType.FLY
        }).catch((e: any) => {
          logger.error(e);
        });
      } else {
        this.sweep
          .moveTo(p.sweep, {
            position: p.position,
            rotation: p.rotation,
            transition: this.sweep.Transition.FLY
          })
          .catch((e: any) => {
            logger.error(e);
          });
      }
    }, 100);
  }
}

export { Sweep, SweepEventsType };
